/**
 * @fileoverview Dashboard Layout
 *
 * Purpose: Root layout for all authenticated dashboard pages.
 * Uses shadcn Sidebar for navigation and ActiveOrgProvider for
 * multi-tenancy context.
 *
 * Wraps: /dashboard, /dashboard/jobs/*, /dashboard/settings/*
 * Protection: Requires authentication (via parent _auth route)
 *
 * Dependencies:
 * - shadcn Sidebar (SidebarProvider, SidebarInset)
 * - AppSidebar component
 * - ActiveOrgProvider context
 * - Convex: users.queries.getCurrentUser, jobs.queries.getJob
 */

import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/ui/layout/sidebar";
import { AppSidebar } from "./-ui.app-sidebar";
import { ActiveOrgProvider } from "@/utils/ActiveOrgProvider";
import { JobPhaseSelect } from "@/components/jobs/JobPhaseSelect";
import {
  CommandPalette,
  CommandPaletteProvider,
} from "@/components/agents/universal/CommandPalette";
import { TimeTrackerProvider } from "@/components/timesheets/TimeTrackerProvider";
import { HeaderTimeTracker } from "@/components/timesheets/HeaderTimeTracker";
import { QuickTimeEntryDialog } from "@/components/timesheets/QuickTimeEntryDialog";
import { AiQuickEntryDialog } from "@/components/timesheets/AiQuickEntryDialog";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout")({
  component: DashboardLayout,
});

function DashboardLayout() {
  const { data: user } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const params = useParams({ strict: false }) as { jobId?: string };
  const jobId = params.jobId;

  const { data: job } = useQuery({
    ...convexQueryOrIdle(
      api.jobs.queries.getJob,
      jobId ? { jobId: jobId as Id<"jobs"> } : undefined,
    ),
    enabled: !!jobId,
  });

  if (!user) {
    return null;
  }

  return (
    <ActiveOrgProvider>
      {/* CommandPaletteProvider wraps the sidebar AND the palette so
          the sidebar "Search" button can call `openPalette()` via
          `useCommandPalette()` without prop drilling. */}
      <CommandPaletteProvider>
        <TimeTrackerProvider>
          <SidebarProvider>
            <AppSidebar user={user} />
            <SidebarInset>
              <div className="flex min-w-0 flex-1 flex-col">
                <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-3">
                  <SidebarTrigger className="-ml-1 h-7 w-7 [&>svg]:size-4" />
                  {job && (
                    <>
                      <h1 className="text-base font-semibold">
                        {job.entityName}
                      </h1>
                      <JobPhaseSelect
                        jobId={job._id}
                        status={job.status}
                        userRole={job.userRole}
                      />
                    </>
                  )}
                  <HeaderTimeTracker />
                </header>
                <Outlet />
              </div>
            </SidebarInset>
            {/* Global Cmd+K / Ctrl+K palette — mounted once at the
                dashboard level so it's available from any sub-route. */}
            <CommandPalette />
            {/* Global quick time entry — opened from the header control,
                the Cmd/Ctrl+Shift+L shortcut, or by stopping the timer. */}
            <QuickTimeEntryDialog />
            {/* Plain-English composer that pre-fills the dialog above. */}
            <AiQuickEntryDialog />
          </SidebarProvider>
        </TimeTrackerProvider>
      </CommandPaletteProvider>
    </ActiveOrgProvider>
  );
}
